import { createAction, props } from '@ngrx/store';
import { ODataTextLine } from '../../odata-types/multiline-text.type';
import { EscalationTimer } from '../escalation-collection.state';
import { NotificationIdState } from '../../utilities/notification-id.type';

// ── Internal store entries ────────────────────────────────────────────────────

/** Recorded in the store when a timer is successfully scheduled. */
export const addEscalationTimerEntry = createAction(
    '[Escalation] Add Timer Entry',
    props<{ notificationId: NotificationIdState; timerHandle: number }>(),
);

/** Removed from the store when a timer is cancelled or fires. */
export const removeEscalationTimerEntry = createAction(
    '[Escalation] Remove Timer Entry',
    props<{ notificationId: NotificationIdState }>(),
);

/** Clears all timer entries from the store, e.g. on a full data reset. */
export const clearEscalationTimerEntries = createAction('[Escalation] Clear Timer Entries');

// ── Intent actions (dispatched by EscalationNgrxService) ─────────────────────

/**
 * Requests that a timeout be scheduled for the given notification.
 * `elapsedHours` is the time already elapsed since OSR start; the effect
 * calculates the remaining delay and creates the `window.setTimeout`.
 */
export const scheduleEscalationTimer = createAction(
    '[Escalation] Schedule Timer',
    props<{ notificationId: NotificationIdState; notificationType: string; elapsedHours: number }>(),
);

/** Requests that the active timer for a notification be cancelled. */
export const cancelEscalationTimer = createAction(
    '[Escalation] Cancel Timer',
    props<{ notificationId: NotificationIdState }>(),
);

/**
 * Triggers the effect that iterates all in-progress notifications and
 * dispatches `scheduleEscalationTimer` for any that need a timer.
 */
export const checkEscalationRequired = createAction('[Escalation] Check Required');

/** Dispatched by the `window.setTimeout` callback when a timer expires. */
export const escalationTimerFired = createAction(
    '[Escalation] Timer Fired',
    props<{ notificationId: NotificationIdState }>(),
);

/** Requests that the escalation popup modal be shown for a notification. */
export const triggerShowEscalationPopup = createAction(
    '[Escalation] Show Popup',
    props<{ notificationId: NotificationIdState }>(),
);

/**
 * Requests that an escalation status be persisted via OData.
 * `escalation` carries the serialisable form payload so the action remains
 * serialisable without embedding a class instance.
 */
export const submitEscalationStatus = createAction(
    '[Escalation] Save Status',
    props<{
        escalationData: {
            notificationId: NotificationIdState;
            messageType: string;
            date: string;
            time: string;
            escalationLText: ODataTextLine[];
        };
    }>(),
);

/** Dispatched by the persistence effect once the IndexedDB data has been read back into the store. */
export const escalationTimersLoadedFromStorage = createAction(
    '[Escalation] Timers loaded from storage',
    props<{ timerEntries: EscalationTimer[] }>(),
);

/** Dispatched by the effect after a successful save chain. */
export const submitEscalationStatusSuccess = createAction(
    '[Escalation] Status Saved',
    props<{ notificationId: NotificationIdState }>(),
);

/** Dispatched by the effect after a failed save chain (error already handled). */
export const submitEscalationStatusError = createAction(
    '[Escalation] Status Failed',
    props<{ notificationId: NotificationIdState }>(),
);

export const hydrateEscalations = createAction('[Escalation] Initialize Escalations');
